/* eslint-disable radix, react/sort-comp */
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
} from '@material-ui/core';
import * as events from '../../../events';
import * as actions from '../../../state';
import { parseUrl } from '../../../utils';

import styles from './styles.module.css';
import LoginMessageSnackbar from './LoginMessageSnackbar';

const messages = defineMessages({
  PASSWORD_REQUIRED: {
    id: 'PASSWORD_REQUIRED',
    defaultMessage: 'Password required',
  },
  SAVE: {
    id: 'SAVE',
    defaultMessage: 'Save',
  },
  NEW_PASSWORD: {
    id: 'NEW_PASSWORD',
    defaultMessage: 'New Password',
  },
  CONFIRM_PASSWORD: {
    id: 'CONFIRM_PASSWORD',
    defaultMessage: 'Confirm Password',
  },
  PASSWORD_RESET_HELPTEXT: {
    id: 'PASSWORD_RESET_HELPTEXT',
    defaultMessage: 'Please set a new password to continue',
  },
  PASSWORD_RESET: {
    id: 'PASSWORD_RESET',
    defaultMessage: 'Password Reset',
  },
  // password rules
  SECURE_PASS_MIN_LENGTH: {
    id: 'SECURE_PASS_MIN_LENGTH',
    defaultMessage: 'Minimum length of {securePassMinLength} characters',
  },
  SECURE_PASS_MIN_CAP_CNT: {
    id: 'SECURE_PASS_MIN_CAP_CNT',
    defaultMessage: 'Minimum of {securePassMinCapCnt} capital letter(s)',
  },
  SECURE_PASS_MIN_NUM_CNT: {
    id: 'SECURE_PASS_MIN_NUM_CNT',
    defaultMessage: 'Minimum of {securePassMinNumCnt} number(s)',
  },
  SECURE_PASS_MIN_SPECIAL_CHAR_CNT: {
    id: 'SECURE_PASS_MIN_SPECIAL_CHAR_CNT',
    defaultMessage: 'Minimum of {securePassMinSpecialCharCnt} special character(s)',
  },
  SECURE_PASS_FORCE_NUM: {
    id: 'SECURE_PASS_FORCE_NUM',
    defaultMessage: 'Numbers only',
  },
  SECURE_PASS_STRONG: {
    id: 'SECURE_PASS_STRONG',
    defaultMessage: 'Your extension cannot be part of your password',
  },
});

function PasswordReset({ intl }) {
  const dispatch = useDispatch();

  const configs = useSelector(actions.selectConfigs);

  const [newPassword, setNewPassword] = useState('');
  const [newPasswordErr, setNewPasswordErr] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [reqAttempted, setReqAttempted] = useState(false);
  const [validationErrs, setValidationErrs] = useState({});

  const securePassMinLength = parseInt(configs?.PORTAL_USERS_SECURE_PASSWORD_MIN_LENGTH) || 8;
  const securePassMinCapCnt = parseInt(configs?.PORTAL_USERS_SECURE_PASSWORD_MIN_CAPITAL_LETTER_COUNT || 0);
  const securePassMinNumCnt = parseInt(configs?.PORTAL_USERS_SECURE_PASSWORD_MIN_NUMBER_COUNT || 0);
  const securePassMinSpecialCharCnt = parseInt(configs?.PORTAL_USERS_SECURE_PASSWORD_MIN_SPECIAL_CHAR_COUNT || 0);
  const securePassForceNum = configs?.PORTAL_USERS_SECURE_PASSWORD_FORCE_NUMERIC;
  const securePassStrong = configs?.PORTAL_USERS_SECURE_PASSWORD_STRONG;
  const allowExtInPass = configs?.PORTAL_USER_SECURE_PASSWORD_ALLOW_EXT_IN_PASSWORD;
  const extensionForbidList = configs?.PORTAL_USER_SECURE_PASSWORD_EXTENSION_FORBID_LIST;

  const onPasswordChange = (e) => {
    setNewPassword(e.target.value);
    setNewPasswordErr(!e.target.value);
  };

  const onConfirmChange = (e) => {
    setConfirmPassword(e.target.value);
  };

  const testPassword = () => {
    let isPasswordValid = true;
    const errors = {};

    // is new password empty
    if (!newPassword) {
      errors.securePassMinLength = true;
      isPasswordValid = false;
    }

    // is new password too short
    if (newPassword.length < securePassMinLength) {
      errors.securePassMinLength = true;
      isPasswordValid = false;
    }

    // does new password have too few capitals
    if (securePassMinCapCnt) {
      const cnt = newPassword.match(/[A-Z]/g) || [];
      if (!cnt || cnt.length < securePassMinCapCnt) {
        errors.securePassMinCapCnt = true;
        isPasswordValid = false;
      }
    }

    // does new password have too few number
    if (securePassMinNumCnt) {
      const cnt = newPassword.match(/\d/g) || [];
      if (!cnt || cnt.length < securePassMinNumCnt) {
        errors.securePassMinNumCnt = true;
        isPasswordValid = false;
      }
    }

    // does new password have too few special characters
    if (securePassMinSpecialCharCnt) {
      const rgx = new RegExp(`[!"#$%&'()*+,-.:;<=>?@[\\]^_\`{|}~]{${securePassMinSpecialCharCnt}}`, 'g');
      if (!rgx.test(newPassword)) {
        errors.securePassMinSpecialCharCnt = true;
        isPasswordValid = false;
      }
    }

    // is new password numeric only
    if (securePassForceNum !== 'no') {
      const cnt = newPassword.match(/!\d/g) || [];
      if (!cnt || cnt.length < securePassForceNum) {
        errors.securePassForceNum = true;
        isPasswordValid = false;
      }
    }

    // is new password "strong"
    if (securePassStrong !== 'no') {
      // does new password contain forbidden strings
      if (extensionForbidList) {
        extensionForbidList.split(',').forEach((forbidWord) => {
          if (newPassword.includes(forbidWord)) {
            errors.securePassStrong = true;
            isPasswordValid = false;
          }
        });
      }

      // does new password contain extension
      if (allowExtInPass !== 'yes') {
        const routeParams = parseUrl(window.location);
        const username = routeParams.login;
        const extension = username.split('@')[0];

        if (newPassword.includes(extension)) {
          errors.securePassStrong = true;
          isPasswordValid = false;
        }
      }
    }

    setValidationErrs(errors);
    setNewPasswordErr(!isPasswordValid);

    return isPasswordValid;
  };

  const passwordRules = () => {
    const rules = [];

    rules.push(
      <span className={validationErrs.securePassMinLength ? styles.failing : styles.passing}>
        {intl.formatMessage(
          { ...messages.SECURE_PASS_MIN_LENGTH },
          { securePassMinLength },
        )}
      </span>,
    );

    if (securePassMinCapCnt) {
      rules.push(
        <span className={validationErrs.securePassMinCapCnt ? styles.failing : styles.passing}>
          {intl.formatMessage(
            { ...messages.SECURE_PASS_MIN_CAP_CNT },
            { securePassMinCapCnt },
          )}
        </span>,
      );
    }

    if (securePassMinNumCnt) {
      rules.push(
        <span className={validationErrs.securePassMinNumCnt ? styles.failing : styles.passing}>
          {intl.formatMessage(
            { ...messages.SECURE_PASS_MIN_NUM_CNT },
            { securePassMinNumCnt },
          )}
        </span>,
      );
    }

    if (securePassMinSpecialCharCnt) {
      rules.push(
        <span className={validationErrs.securePassMinSpecialCharCnt ? styles.failing : styles.passing}>
          {intl.formatMessage(
            { ...messages.SECURE_PASS_MIN_SPECIAL_CHAR_CNT },
            { securePassMinSpecialCharCnt },
          )}
        </span>,
      );
    }

    if (securePassForceNum && securePassForceNum !== 'no') {
      rules.push(
        <span className={validationErrs.securePassForceNum ? styles.failing : styles.passing}>
          {intl.formatMessage({ ...messages.SECURE_PASS_FORCE_NUM })}
        </span>,
      );
    }

    if (securePassStrong !== 'no' && allowExtInPass !== 'yes') {
      rules.push(
        <span className={validationErrs.securePassStrong ? styles.failing : styles.passing}>
          {intl.formatMessage({ ...messages.SECURE_PASS_STRONG })}
        </span>,
      );
    }

    return rules;
  };

  const passwordResetSubmit = (e) => {
    e.preventDefault();

    setNewPasswordErr(false);
    setReqAttempted(true);

    if (!testPassword()) {
      // nothing if testPassword fails
    } else if (newPassword !== confirmPassword) {
      // this.setState({ confirmPasswordErr: true });
    } else {
      dispatch(events.passwordReset(newPassword));
    }
  };

  return (
    <div className={`${styles.loginCard} animate__animated animate__fadeIn`}>
      <Card className={styles.card} style={{ textAlign: 'left' }}>
        <CardHeader
          style={{ paddingLeft: 32 }}
          title={intl.formatMessage({ ...messages.PASSWORD_RESET })}
        />
        <CardContent className={styles.content} classes={{ root: styles.loginFormCard }}>
          <div style={{ color: '#333', paddingLeft: 16 }}>
            <FormattedMessage {...messages.PASSWORD_RESET_HELPTEXT} />
          </div>
          <form onSubmit={passwordResetSubmit} noValidate style={{ marginTop: 7 }}>
            <TextField
              required
              id="newPassword"
              type="password"
              onFocus={() => setReqAttempted(false)}
              onBlur={testPassword}
              onChange={onPasswordChange}
              className={styles.textField}
              label={intl.formatMessage({ ...messages.NEW_PASSWORD })}
              error={reqAttempted && Boolean(newPasswordErr)}
            />
            <TextField
              required
              id="confirmPassword"
              type="password"
              style={{ marginTop: 12 }}
              onFocus={() => setReqAttempted(false)}
              className={styles.textField}
              onBlur={onConfirmChange}
              onChange={onConfirmChange}
              label={intl.formatMessage({ ...messages.CONFIRM_PASSWORD })}
              error={reqAttempted && newPassword !== confirmPassword}
            />
            <div className={styles.pwdRules}>
              {passwordRules()}
            </div>
            <Button
              fullWidth
              size="large"
              type="submit"
              color="primary"
              variant="contained"
              className={styles.loginBtn}
            >
              <FormattedMessage {...messages.SAVE} />
            </Button>
          </form>
        </CardContent>
      </Card>
      <LoginMessageSnackbar />
    </div>
  );
}

PasswordReset.propTypes = {
  intl: PropTypes.shape({}).isRequired,
};

export default injectIntl(PasswordReset);
